// Pure weather icon utility functions - extracted from Forecast.vue

export const EMOJI_PRIORITY = [
  { emoji: '🏠', key: 'stayHome' },
  { emoji: '🚘', key: 'driveSlowly' },
  { emoji: '☂️', key: 'bringUmbrella' },
  { emoji: '🧥', key: 'wearJacket' },
  { emoji: '🏃', key: 'exerciseOutdoors' },
  { emoji: '👕', key: 'hangClothes' },
  { emoji: '🔥', key: 'heating' },
  { emoji: '❄️', key: 'airConditioning' }
] as const

export const iconGridLayout = [
  ['03:00', '06:00', '09:00', '12:00'],  // Row 1: 3 AM, 6 AM, 9 AM, Noon
  ['15:00', '18:00', '21:00', '24:00']   // Row 2: 3 PM, 6 PM, 9 PM, Midnight
] as const

export function getWeatherIcon(precipitation: number, cloudCover: number, wind: number, isDay: boolean): string {
  if (precipitation >= 8) return '310'  // Heavy rain
  if (wind >= 6) return '2001'  // Wind
  if (precipitation >= 3) return '306'  // Moderate rain
  if (precipitation >= 1) return '305'  // Light rain
  if (cloudCover === 7 || cloudCover === 8) return '104'  // Overcast
  if (cloudCover === 6) return isDay ? '101' : '151'  // Cloudy or Cloudy night
  if (cloudCover === 4 || cloudCover === 5) return isDay ? '103' : '153'  // Sunny intervals
  if (cloudCover === 2 || cloudCover === 3) return isDay ? '102' : '152'  // Sunny periods
  return isDay ? '100' : '150'  // Sunny or Clear night
}

export function getWeatherEmoji(
  precipitation: number,
  wind: number,
  temperature: number,
  timeStr: string,
  humidity: number,
  date: Date,
  latitude?: number,
  longitude?: number
): string {
  const hour = parseInt(timeStr.split(':')[0], 10)
  const isMidnight = hour === 0
  const is3AM = hour === 3
  const isSecondMidnight = timeStr === '24:00'

  const applicableEmojis: string[] = []

  if (precipitation >= 3 || wind >= 6) applicableEmojis.push('🏠')
  if (precipitation >= 3 || humidity >= 95) applicableEmojis.push('🚘')
  if (precipitation >= 2) applicableEmojis.push('☂️')
  if ((wind >= 5 || precipitation >= 1) && temperature <= 27) applicableEmojis.push('🧥')
  if (precipitation === 0 && wind <= 5 && !isMidnight && !is3AM && !isSecondMidnight) applicableEmojis.push('🏃')
  if (humidity <= 65 && precipitation === 0) applicableEmojis.push('👕')

  if (latitude !== undefined && longitude !== undefined) {
    const month = date.getMonth() + 1
    const isNorthernHemisphere = latitude >= 0
    let isSummer = false
    let isWinter = false

    if (isNorthernHemisphere) {
      isSummer = month >= 6 && month <= 8
      isWinter = month === 12 || month <= 2
    } else {
      isSummer = month === 12 || month <= 2
      isWinter = month >= 6 && month <= 8
    }

    if (temperature <= 11 && !isSummer) applicableEmojis.push('🔥')
    if (temperature >= 28 && !isWinter) applicableEmojis.push('❄️')
  } else {
    if (temperature <= 11) applicableEmojis.push('🔥')
    if (temperature >= 28) applicableEmojis.push('❄️')
  }

  const prioritizedEmojis = EMOJI_PRIORITY
    .filter(item => applicableEmojis.includes(item.emoji))
    .slice(0, 3)
    .map(item => item.emoji)

  return prioritizedEmojis.join('') || ''
}

const ICON_DESCRIPTIONS: Record<string, string> = {
  '100': 'Clear sky',
  '101': 'Cloudy',
  '102': 'Sunny periods',
  '103': 'Sunny intervals',
  '104': 'Overcast',
  '150': 'Clear sky (night)',
  '151': 'Cloudy (night)',
  '152': 'Sunny periods (night)',
  '153': 'Sunny intervals (night)',
  '300': 'Light rain', '301': 'Moderate rain', '302': 'Heavy rain',
  '303': 'Light rain', '304': 'Moderate rain', '305': 'Light rain',
  '306': 'Moderate rain', '307': 'Heavy rain', '308': 'Heavy rain',
  '309': 'Heavy rain', '310': 'Heavy rain', '311': 'Heavy rain',
  '312': 'Heavy rain', '313': 'Heavy rain', '314': 'Heavy rain',
  '315': 'Heavy rain', '316': 'Heavy rain', '317': 'Heavy rain',
  '318': 'Heavy rain',
  '350': 'Light snow', '351': 'Moderate snow', '399': 'Heavy snow',
  '400': 'Light snow', '401': 'Moderate snow', '402': 'Heavy snow',
  '403': 'Heavy snow', '404': 'Heavy snow', '405': 'Heavy snow',
  '406': 'Heavy snow', '407': 'Heavy snow', '408': 'Heavy snow',
  '409': 'Heavy snow', '410': 'Heavy snow',
  '456': 'Light snow', '457': 'Moderate snow', '499': 'Heavy snow',
  '500': 'Light snow', '501': 'Moderate snow', '502': 'Heavy snow',
  '503': 'Heavy snow', '504': 'Heavy snow', '507': 'Heavy snow',
  '508': 'Heavy snow', '509': 'Heavy snow', '510': 'Heavy snow',
  '511': 'Heavy snow', '512': 'Heavy snow', '513': 'Heavy snow',
  '514': 'Heavy snow', '515': 'Heavy snow',
  '800': 'Clear sky', '801': 'Few clouds', '802': 'Scattered clouds',
  '803': 'Broken clouds', '804': 'Overcast', '805': 'Overcast',
  '806': 'Overcast', '807': 'Overcast',
  '900': 'Unknown weather',
  '2001': 'Wind'
}

export function getWeatherIconDescription(iconCode: string): string {
  return ICON_DESCRIPTIONS[iconCode] || 'Unknown weather'
}

const ICON_NAMES: Record<string, string> = {
  '100': 'Clear sky', '101': 'Cloudy', '102': 'Sunny periods',
  '103': 'Sunny intervals', '104': 'Overcast',
  '150': 'Clear sky', '151': 'Cloudy', '152': 'Sunny periods', '153': 'Sunny intervals',
  '300': 'Light rain', '301': 'Moderate rain', '302': 'Heavy rain',
  '303': 'Light rain', '304': 'Moderate rain', '305': 'Light rain',
  '306': 'Moderate rain', '307': 'Heavy rain', '308': 'Heavy rain',
  '309': 'Heavy rain', '310': 'Heavy rain', '311': 'Heavy rain',
  '312': 'Heavy rain', '313': 'Heavy rain', '314': 'Heavy rain',
  '315': 'Heavy rain', '316': 'Heavy rain', '317': 'Heavy rain',
  '318': 'Heavy rain',
  '350': 'Light snow', '351': 'Moderate snow', '399': 'Heavy snow',
  '400': 'Light snow', '401': 'Moderate snow', '402': 'Heavy snow',
  '403': 'Heavy snow', '404': 'Heavy snow', '405': 'Heavy snow',
  '406': 'Heavy snow', '407': 'Heavy snow', '408': 'Heavy snow',
  '409': 'Heavy snow', '410': 'Heavy snow',
  '456': 'Light snow', '457': 'Moderate snow', '499': 'Heavy snow',
  '500': 'Light snow', '501': 'Moderate snow', '502': 'Heavy snow',
  '503': 'Heavy snow', '504': 'Heavy snow', '507': 'Heavy snow',
  '508': 'Heavy snow', '509': 'Heavy snow', '510': 'Heavy snow',
  '511': 'Heavy snow', '512': 'Heavy snow', '513': 'Heavy snow',
  '514': 'Heavy snow', '515': 'Heavy snow',
  '800': 'Clear sky', '801': 'Few clouds', '802': 'Scattered clouds',
  '803': 'Broken clouds', '804': 'Overcast', '805': 'Overcast',
  '806': 'Overcast', '807': 'Overcast',
  '900': 'Unknown',
  '2001': 'Wind'
}

export function getWeatherIconName(iconCode: string): string {
  return ICON_NAMES[iconCode] || 'Unknown'
}

export function isDayTime(
  hour: number,
  _date: Date,
  _latitude?: number,
  _longitude?: number,
  sunrise?: string,
  sunset?: string
): boolean {
  if (sunrise && sunset) {
    try {
      const [sunriseHour, sunriseMinute] = sunrise.split(':').map(Number)
      const [sunsetHour, sunsetMinute] = sunset.split(':').map(Number)
      const sunriseMinutes = sunriseHour * 60 + sunriseMinute
      const sunsetMinutes = sunsetHour * 60 + sunsetMinute
      const currentMinutes = hour * 60

      if (sunsetMinutes < sunriseMinutes) {
        return currentMinutes >= sunriseMinutes || currentMinutes <= sunsetMinutes
      } else {
        return currentMinutes >= sunriseMinutes && currentMinutes <= sunsetMinutes
      }
    } catch (error) {
      console.warn('Failed to parse sunrise/sunset times from API:', error)
      return true
    }
  }
  return true
}

export function formatTimeStandard(timeStr: string): string {
  switch (timeStr) {
    case '00:00':
    case '24:00': return '12 AM'
    case '03:00': return '3 AM'
    case '06:00': return '6 AM'
    case '09:00': return '9 AM'
    case '12:00': return '12 PM'
    case '15:00': return '3 PM'
    case '18:00': return '6 PM'
    case '21:00': return '9 PM'
    default: return timeStr
  }
}

export function getTimeLabel(timeStr: string): string {
  return formatTimeStandard(timeStr)
}

export function isTimeInPast(timeStr: string, timezone: string): boolean {
  try {
    const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10))
    if (isNaN(hours) || isNaN(minutes)) return false

    const now = new Date()
    let currentHour: number
    let currentMinute: number

    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(now)
      currentHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10)
      currentMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10)
    } catch {
      currentHour = now.getUTCHours()
      currentMinute = now.getUTCMinutes()
    }

    return (hours * 60 + minutes) < (currentHour * 60 + currentMinute - 30)
  } catch {
    return false
  }
}
